import { useMemo } from 'react';

import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { type ImportSourceType } from '~/utils/ImportUtils';

export type ExcludedIntegrationType = 'slack' | ImportSourceType;

const VALID_INTEGRATIONS: readonly ExcludedIntegrationType[] = [
  // integrations
  'slack',
  // import/clip sources
  'dropbox',
  'googleDrive',
  'box',
  'brandfolder',
  'sharePoint',
] as const;

const isValidIntegration = (integration: string): integration is ExcludedIntegrationType => {
  return VALID_INTEGRATIONS.some((validIntegration) => validIntegration === integration);
};

export const useExcludedIntegrationsFlag = () => {
  const { excludedIntegrations: excludedIntegrationsFlag } = useFeatureFlags();

  const list = useMemo(() => {
    if (
      !excludedIntegrationsFlag ||
      !Array.isArray(excludedIntegrationsFlag) ||
      excludedIntegrationsFlag.length === 0
    ) {
      return [];
    }

    return excludedIntegrationsFlag.map((integration) => String(integration).trim()).filter(isValidIntegration);
  }, [excludedIntegrationsFlag]);

  const excludedIntegrations = useMemo(() => list.filter((integration) => integration === 'slack'), [list]);
  const excludedImportSources = useMemo(() => list.filter((integration) => integration !== 'slack'), [list]);

  return {
    excludedIntegrations,
    excludedImportSources,
  };
};
